import React, {isValidElement} from 'react';
import classNames from 'classnames';
import {Box, Button, ButtonProps, Divider} from '@bosch/react-frok';
import {DivDialogBody, DivHeadline, DivTitle} from './CustomDownloadFileDialog.style';
import {invoke} from './invoke';

export interface CustomDialogProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
    // name of the dialog window
    readonly title?: string;
    // variant identifier,
    readonly variant?: 'info' | 'error' | 'warning' | 'success';
    // a page-unique identifier
    readonly dialogId?: string;
    // if true, show as modal
    readonly modal?: boolean;
    // if true, open modal dialog
    readonly open?: boolean;
    // The headline of the dialog box content
    readonly headline?: string;
    // The error code of the dialog box
    readonly dialogCode?: string;
    /**
     * Label for the confirm button
     */
    readonly cancelLabel?: string;
    /**
     * Confirm button slot.
     */
    readonly confirmLabel?: string;
    /**
     * Label for the cancel button
     */
    cancelButton?: ButtonProps | React.ReactElement<ButtonProps>;
    /**
     * Callback for the confirm button
     */
    confirmButton?: ButtonProps | React.ReactElement<ButtonProps>;
    /**
     * Cancel button slot.
     */
    onCancel?: (event: React.MouseEvent) => void;
    /**
     * Callback for the 'X' close button, button isn't rendered if not provided
     */
    onConfirm?: (event: React.MouseEvent) => void;
    /**
     * Callback for the cancel button
     */
    onClose?: (event: React.MouseEvent) => void;
}

export const CustomDownloadFileDialog = React.forwardRef<HTMLDivElement, CustomDialogProps>(
    (
        {
            title,
            variant,
            modal = false,
            open = false,
            dialogId,
            headline,
            dialogCode,
            children,
            cancelLabel,
            confirmLabel,
            cancelButton,
            confirmButton,
            onCancel,
            onConfirm,
            onClose,
            className,
            ...rest
        },
        ref,
    ) => {
        const prefixedDialogId = dialogId || 'dialog';
        const dialogTitleId = `${prefixedDialogId}-title`;
        const dialogDescriptionId = `${prefixedDialogId}-description`;

        const dialogClasses = classNames(
            ['m-dialog', {[`m-dialog--${variant}`]: variant}],
            className,
        );

        const getSlot = (
            type: React.FunctionComponent,
            slotProperty: object | undefined,
            defaultProps?: object,
            overrideProps?: object,
        ): JSX.Element | null => {
            const Comp = type;
            if (typeof slotProperty === 'object') {
                console.log('inside else if');
                return (
                    <Comp
                        {...defaultProps}
                        {...slotProperty}
                        {...overrideProps}
                    />
                );
            } else if (typeof slotProperty === 'undefined') {
                console.log(typeof Comp);
                console.log(typeof defaultProps);
                console.log(typeof overrideProps);
                console.log(typeof slotProperty);
                return <Comp {...defaultProps} {...overrideProps} />;
            }

            return null;
        };

        const handleConfirmClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
            if (typeof onConfirm === 'function') {
                onConfirm(ev);
            }
            if (isValidElement(confirmButton)) {
                invoke(confirmButton.props, 'onClick', ev);
            } else if (typeof confirmButton === 'object') {
                invoke(confirmButton, 'onClick', ev);
            }
        };

        const handleCancelClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
            if (typeof onCancel === 'function') {
                onCancel(ev);
            }
            if (isValidElement(cancelButton)) {
                invoke(cancelButton.props, 'onClick', ev);
            } else if (typeof cancelButton === 'object') {
                invoke(cancelButton, 'onClick', ev);
            }
        };

        const renderActions = () => {
            const ConfirmButton = getSlot(Button, confirmButton, {
                label: confirmLabel || 'OK',
                mode: 'primary',
                action: 'confirm',
            }, {
                onClick: handleConfirmClick,
            });

            const CancelButton = getSlot(Button, cancelButton, {
                label: cancelLabel || 'Cancel',
                mode: 'secondary',
                action: 'cancel',
            }, {
                onClick: handleCancelClick,
            });

            return (
                (onConfirm || onCancel || confirmButton || cancelButton) && (
                    <div className='m-dialog__actions'>
                        {(onConfirm || confirmButton) && ConfirmButton}
                        {(onCancel || cancelButton) && CancelButton}
                    </div>
                )
            );
        };

        return (
            <div
                className={dialogClasses}
                id={prefixedDialogId}
                role='dialog'
                aria-labelledby={dialogTitleId}
                {...rest}
                ref={ref}
            >
                <Box
                    modal={modal}
                    open={open}
                    modalId={`modal-for-${prefixedDialogId}`}
                >
                    {title && (
                        <>
                            {variant && (
                                <div
                                    className={`m-dialog__remark --${variant}`}
                                />
                            )}
                            <div className='m-dialog__header'>
                                {variant && (
                                    <i
                                        className={`a-ui-icon a-ui-icon--ui-ic-${variant}`}
                                    />
                                )}
                                <DivTitle>{title}</DivTitle>
                                {onClose && (
                                    <Button
                                        action='close'
                                        mode='integrated'
                                        icon={{
                                            iconName: 'close',
                                            isUiIcon: true,
                                        }}
                                        aria-label='close dialog'
                                        onClick={onClose}
                                    />
                                )}
                            </div>
                            <Divider/>
                        </>
                    )}
                    <div className='m-dialog__content'>
                        {headline && (
                            <DivHeadline
                                id={dialogTitleId}
                            >
                                {headline}
                            </DivHeadline>
                        )}
                        <DivDialogBody
                            id={dialogDescriptionId}
                        >
                            {children}
                        </DivDialogBody>
                        {dialogCode && (
                            <div>{dialogCode}</div>
                        )}
                        {renderActions()}
                    </div>
                </Box>
            </div>
        );
    },
);

CustomDownloadFileDialog.displayName = 'Dialog';
